import { Button } from '@/components/PageHeading';
import { StatusScreen } from '@/components/StatusScreen';

/**
 * Something threw while rendering, and React tore the tree down.
 *
 * The alternative to this screen is not a smaller failure — it is a white page, because an
 * uncaught error in a React 18 tree unmounts the whole thing. So the only real question is whether
 * the user is told what happened, and by whom.
 *
 * Reloading is the only offer, and it is an honest one: the state that produced the crash lives in
 * memory and nothing here can repair it. The refresh cookie survives a reload, so the session
 * usually does too — see the silent refresh in `App`.
 */
export function ServerErrorPage({ detail }: { detail?: string }) {
  return (
    <StatusScreen
      standalone
      code="500"
      // The template's own 500 glyph. See the note on `NotFoundPage`.
      icon="ph-duotone ph-x-circle"
      tone="danger"
      title="Something went wrong"
      message={
        <>
          The screen stopped unexpectedly. Nothing you had already saved is affected — this happened
          in the browser, not on the server.
          {detail && (
            <span className="font-mono-nums mt-3 block rounded-md border border-border-color bg-white px-3 py-2 text-left text-xs break-words text-default">
              {detail}
            </span>
          )}
        </>
      }
      actions={
        <Button
          variant="primary"
          icon="icon icon-rotate-cw"
          onClick={() => window.location.reload()}
        >
          Reload AttireRent
        </Button>
      }
    />
  );
}
